import { StateCreator } from 'zustand';
import { TInvitesSlice, TUsersStore } from './types';
import { usersApi } from '@/API';
import { convertKeys } from '@/utils/common';
import { TInviteType, TUserInviteCamel } from '@/types';
import dayjs from 'dayjs';

export const invitesSlice: StateCreator<TUsersStore, [], [], TInvitesSlice> = (
  set,
  get,
) => ({
  loadingInvites: false,

  invites: [],

  /**
   * Fetches both the pending and expired invites for an institution and
   * replaces `invites` with their combined list.
   * @param institutionId - The institution to fetch invites for.
   */
  fetchInvites: async (institutionId: string) => {
    set({ loadingInvites: true });
    try {
      const [resPending, resExpired] = await Promise.all([
        usersApi.getInvites(institutionId, 'pending'),
        usersApi.getInvites(institutionId, 'expired'),
      ]);
      const dataPending = convertKeys(resPending.data.data);
      const dataExpired = convertKeys(resExpired.data.data);
      set({ invites: [...dataPending, ...dataExpired] });
    } catch (error) {
      set({ invites: [] });
      console.error(error);
      throw error;
    } finally {
      set({ loadingInvites: false });
    }
  },

  /**
   * Derives the invites currently in the given state from `invites`. An
   * invite is "expired" once it has been cancelled or has passed its expiry
   * date, and "pending" while it isn't expired and isn't accepted. Accepted
   * invites that haven't expired belong to neither state.
   * @param type - Which invite state to return.
   * @returns The invites in `invites` matching that state.
   */
  getInvitesByType: (type: TInviteType) => {
    return get().invites.filter((item) => {
      const isExpired = item.cancelledAt || dayjs().isAfter(item.expiresAt);
      const isPending = !isExpired && !item.isAccepted;
      return type === 'pending' ? isPending : isExpired;
    });
  },

  /**
   * Cancels a pending invite via the API.
   * @param inviteId - The id of the invite to cancel.
   */
  cancelInvitation: async (inviteId: string) => {
    try {
      await usersApi.cancelInvite(inviteId);
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  /**
   * Merges an invite into `invites`, updating the existing entry in place or
   * appending it when it isn't present yet.
   * @param data - The invite to merge in.
   */
  updateInvite: (data: TUserInviteCamel) => {
    const { invites } = get();
    const findInviteIndex = invites.findIndex((item) => item.id === data.id);
    if (findInviteIndex < 0) {
      set({ invites: [...invites, data] });
      return;
    }
    const prev = invites[findInviteIndex];
    const merged = { ...prev, ...data };
    const newInvites = [...invites];
    newInvites[findInviteIndex] = merged;
    set({ invites: newInvites });
  },
});
